import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from './icons';

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * Badge: a small square tag (FILM, DVD), or a round count (`count`).
 * Tones: neutral, ink, accent, amber, green, danger, outline.
 */
export function Badge({ tone = 'neutral', count = false, className, children, ...rest }) {
  return (
    <span className={cx('ui-badge', `ui-badge--${tone}`, count && 'ui-badge--count', className)} {...rest}>
      {children}
    </span>
  );
}

/** Pill: a rounded status label (lecture / modification, Déjà dans la collection). */
export function Pill({ tone = 'neutral', className, children, ...rest }) {
  return (
    <span className={cx('ui-pill', `ui-pill--${tone}`, className)} {...rest}>
      {children}
    </span>
  );
}

/**
 * Chip: a filter or tag. With onRemove it is a button whose accessible name
 * says what it removes.
 */
export function Chip({ label, value, onRemove, removeLabel, className, selected = false, onClick, children }) {
  const { t } = useTranslation();
  const content = (
    <>
      {label && <span className="ui-chip__key">{label}{value != null && t('ui.colon')}</span>}
      {value != null ? <span className="ui-chip__value">{value}</span> : children}
    </>
  );
  if (onRemove) {
    const name = removeLabel || t('ui.removeFilter', { name: [label, value ?? children].filter(Boolean).join(`${t('ui.colon')} `) });
    return (
      <button type="button" className={cx('ui-chip', 'ui-chip--removable', className)} onClick={onRemove} aria-label={name}>
        {content}
        <CloseIcon size={16} />
      </button>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={cx('ui-chip', selected && 'is-selected', className)} aria-pressed={selected} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <span className={cx('ui-chip', 'ui-chip--static', className)}>{content}</span>;
}

export default Badge;
